import type { ReactNode } from "react";
import { cn } from "./utils";

// Size utilities live in SIZES, not BASE: two competing h-* classes would be resolved by
// stylesheet order, not by the order they appear here, and the smaller one lost.
const BASE = "group inline-flex items-center rounded-full border transition duration-200 ease-soft";

const SIZES = {
  md: "h-11 gap-2 px-5 text-sm font-medium",
  sm: "h-7 gap-1.5 px-3 text-[12px] font-normal",
} as const;

const VARIANTS = {
  primary: "border-transparent bg-btn text-btn-fg hover:-translate-y-px",
  accent: "border-transparent bg-accent text-white hover:-translate-y-px",
  ghost: "border-line-strong bg-transparent text-fg hover:border-fg-muted",
} as const;

export type ButtonVariant = keyof typeof VARIANTS;

/** A link styled as a button. `arrow` adds the trailing → that nudges on hover. */
export function ButtonLink({
  href,
  variant,
  arrow = false,
  size = "md",
  external = false,
  children,
}: {
  href: string;
  variant: ButtonVariant;
  arrow?: boolean;
  size?: "md" | "sm";
  /** Opens in a new tab, with the rel that stops the new page reaching back into this one. */
  external?: boolean;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      className={cn(BASE, SIZES[size], VARIANTS[variant])}
    >
      {children}
      {arrow && (
        <span aria-hidden className="transition-transform duration-200 ease-soft group-hover:translate-x-[3px]">
          →
        </span>
      )}
    </a>
  );
}
